import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { PortalEmbed, FileListItem } from '@flowportals/portal-client';

import { PencilSpinnerComponent } from 'src/app/ui/pencil-spinner/pencil-spinner.component';
import { FormatBytesPipe } from 'src/app/pipes/format-bytes.pipe';
import { NotificationToasterService } from 'src/app/services/notification-toaster.service';
import { TelemetryService } from 'src/app/services/telemetry.service';
import { downloadBlob } from 'src/app/utilities/FileUtils';
import { IconTypes } from '../pencil-icon/pencil-icon.component';
import {
  ModalTemplateComponent,
  PencilModalSizeEnum,
} from '../modals/modal-template/modal-template.component';
import {
  PencilButtonComponent,
  PencilButtonThemeEnum,
} from '../pencil-button/pencil-button.component';

// Extended FileListItem with component-specific properties
interface FileListItemWithDownloadState extends FileListItem {
  isDownloading?: boolean;
}

export interface PortalFileManagerData {
  portalEmbed: PortalEmbed;
}

@Component({
  selector: 'app-portal-file-manager',
  standalone: true,
  imports: [
    CommonModule,
    PencilButtonComponent,
    PencilSpinnerComponent,
    ModalTemplateComponent,
    FormatBytesPipe,
    TranslateModule,
  ],
  templateUrl: './portal-file-manager.component.html',
  styleUrls: ['./portal-file-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PortalFileManagerComponent implements OnInit {
  private readonly filesSubject$ = new BehaviorSubject<FileListItemWithDownloadState[]>([]);
  readonly loading$ = new BehaviorSubject<boolean>(false);

  // Expose files as observable for template
  readonly files$: Observable<FileListItemWithDownloadState[]>;

  readonly PencilButtonThemeEnum = PencilButtonThemeEnum;
  readonly PencilModalSizeEnum = PencilModalSizeEnum;
  readonly IconTypes = IconTypes;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PortalFileManagerData,
    private dialogRef: MatDialogRef<PortalFileManagerComponent>,
    private notificationService: NotificationToasterService,
    private translateService: TranslateService,
    private telemetryService: TelemetryService,
  ) {
    this.files$ = this.filesSubject$.asObservable();
  }

  ngOnInit(): void {
    this.loadFiles();
  }

  async loadFiles(): Promise<void> {
    if (this.loading$.value) {
      return;
    }

    this.setLoading(true);

    try {
      const files = await this.data.portalEmbed.listFiles();
      // Map files to include isDownloading property
      const filesWithState: FileListItemWithDownloadState[] = (files || []).map((file) => ({
        ...file,
        isDownloading: false,
      }));
      this.filesSubject$.next(filesWithState);
    } catch (error) {
      this.handleError(
        error,
        this.translateService.instant('Failed to load files'),
        this.notificationService.generateNotificationCode(),
      );
      this.filesSubject$.next([]);
    } finally {
      this.setLoading(false);
    }
  }

  async refreshFiles(): Promise<void> {
    try {
      await this.data.portalEmbed.refreshFileList();
      await this.loadFiles();
    } catch (error) {
      this.handleError(
        error,
        this.translateService.instant('Failed to refresh file list'),
        this.notificationService.generateNotificationCode(),
      );
    }
  }

  async downloadFile(file: FileListItemWithDownloadState): Promise<void> {
    if (file.isDownloading) {
      return;
    }

    this.setFileDownloading(file, true);

    try {
      const blob = await this.data.portalEmbed.downloadFile(file.name);
      downloadBlob(blob, file.name);

      this.notificationService.showDefaultSuccessNotification(
        this.translateService.instant('File downloaded successfully'),
        this.notificationService.generateNotificationCode(),
      );
    } catch (error) {
      this.handleError(
        error,
        this.translateService.instant('Failed to download file: {{filename}}', {
          filename: file.name,
        }),
        this.notificationService.generateNotificationCode(),
      );
    } finally {
      this.setFileDownloading(file, false);
    }
  }

  private handleError(error: unknown, message: string, notificationCode: string): void {
    const errorMessage =
      error instanceof Error ? error.message : this.translateService.instant('Unknown error');

    // Log error to telemetry for debugging and monitoring
    this.telemetryService.errorEvent('portal_file_manager_error', {
      message,
      errorMessage,
      error: error instanceof Error ? error.stack : String(error),
      notificationCode,
    });

    this.notificationService.showDefaultErrorNotificationV2(
      message,
      errorMessage,
      notificationCode,
    );
  }

  trackByFileName(_index: number, file: FileListItemWithDownloadState): string {
    return file.name;
  }

  handleClose(event: Event): void {
    this.dialogRef.close();
  }

  private setLoading(loading: boolean): void {
    this.loading$.next(loading);
  }

  private setFileDownloading(file: FileListItemWithDownloadState, downloading: boolean): void {
    const currentFiles = this.filesSubject$.value;
    const updatedFiles = currentFiles.map((f) =>
      f.name === file.name ? { ...f, isDownloading: downloading } : f,
    );
    this.filesSubject$.next(updatedFiles);
  }
}
